"use client";

import { type HTMLAttributes, forwardRef, useState } from "react";

import Image from "next/image";

import { type VariantProps, tv } from "tailwind-variants";

const avatarVariants = tv({
  base: "relative overflow-hidden rounded-full border-solid object-contain font-medium",
  variants: {
    size: {
      sm: "size-8 border-2 text-sm",
      md: "size-12 border-4 text-xl",
      ml: "size-[60px] border-4 text-2xl",
      lg: "size-16 sm:size-28 sm:border-8 sm:text-4xl",
    },
    color: {
      primary: "border-aubergine-perl-500",
      secondary: "border-lilac-geode-300",
      white: "border-dr-white-600",
    },
    active: {
      true: "border-lilac-geode-300",
    },
  },
  defaultVariants: {
    size: "lg",
    color: "primary",
  },
});

export type AvatarVariantsProps = VariantProps<typeof avatarVariants>;

interface LetterAvatarProps extends Omit<AvatarProps, "src"> {
  initialLetters: string;
}

export const LetterAvatar = ({
  size,
  color,
  className,
  active,
  initialLetters,
}: LetterAvatarProps) => (
  <div
    className={`${avatarVariants({ size, color, className, active })} flex items-center justify-center rounded-full bg-aubergine-perl-100 text-aubergine-perl-400`}
  >
    <span className="font-poppins"> {initialLetters}</span>
  </div>
);

interface AvatarProps
  extends HTMLAttributes<HTMLImageElement>,
    AvatarVariantsProps {
  color?: "primary" | "secondary" | "white";
  src?: string;
  alt?: string;
  userName?: string;
}

const Avatar = forwardRef<HTMLImageElement, AvatarProps>(
  ({ className, size, color, active, src, alt, userName, ...props }, ref) => {
    const [imgSrc, setImgSrc] = useState(src);

    const getInitialsLetters = (fullName: string) => {
      const [firstName, ...lastNames] = fullName.trim().split(" ");
      const initials = lastNames.length
        ? `${firstName?.charAt(0).toUpperCase()}${lastNames?.pop()?.charAt(0).toUpperCase()}`
        : firstName?.charAt(0).toUpperCase();

      return initials;
    };

    return (
      <>
        {imgSrc ? (
          <div className="relative flex">
            <Image
              className={`${avatarVariants({ size, color, className, active })}`}
              alt={alt ?? "Foto de Perfil"}
              ref={ref}
              src={imgSrc}
              sizes="100vw"
              onError={() => {
                setImgSrc("");
              }}
              width={512}
              height={512}
              priority
              {...props}
            />
          </div>
        ) : (
          <>
            {userName && (
              <LetterAvatar
                color={color}
                size={size}
                initialLetters={getInitialsLetters(userName ?? "")!}
                active={active}
                className={className}
              />
            )}
          </>
        )}
      </>
    );
  },
);

Avatar.displayName = "Avatar";

export default Avatar;
